import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, MoveHorizontal } from "lucide-react";

/**
 * BeforeAfterSlider
 *
 * Draggable before/after comparison slider with arrow navigation
 * for multiple items.
 *
 * Props:
 * items: Array<{ id?, before_image, after_image, title?, caption?, sort_order? }>
 * showLabels?: boolean
 */
export default function BeforeAfterSlider({ items = [], showLabels = true }) {
  const cleaned = (items || [])
    .filter(
      (it) =>
        it &&
        (it.before_image || it.beforeImage) &&
        (it.after_image || it.afterImage)
    )
    .map((it) => ({
      id: it.id,
      before_image: it.before_image || it.beforeImage,
      after_image: it.after_image || it.afterImage,
      title: it.title || "",
      caption: it.caption || "",
    }));

  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (activeIndex > cleaned.length - 1) {
      setActiveIndex(0);
    }
  }, [cleaned.length, activeIndex]);

  if (!cleaned.length) return null;

  const current = cleaned[activeIndex];

  const goPrev = () => {
    setActiveIndex((i) => (i === 0 ? cleaned.length - 1 : i - 1));
  };

  const goNext = () => {
    setActiveIndex((i) => (i === cleaned.length - 1 ? 0 : i + 1));
  };

  return (
    <div className="w-full">
      <div className="relative">
        <BeforeAfterCanvas
          key={current.id || activeIndex}
          before={current.before_image}
          after={current.after_image}
          showLabels={showLabels}
        />

        {cleaned.length > 1 ? (
          <>
            <button
              type="button"
              onClick={goPrev}
              aria-label="Previous before/after comparison"
              className="absolute left-3 top-1/2 -translate-y-1/2 z-20 h-11 w-11 md:h-12 md:w-12 grid place-items-center rounded-full border border-white/20 bg-ink-black/85 text-brand-white backdrop-blur transition-all duration-300 hover:border-brand-yellow hover:text-brand-yellow hover:scale-105 shadow-2xl"
              data-testid="ba-prev"
            >
              <ChevronLeft className="h-5 w-5 md:h-6 md:w-6" />
            </button>

            <button
              type="button"
              onClick={goNext}
              aria-label="Next before/after comparison"
              className="absolute right-3 top-1/2 -translate-y-1/2 z-20 h-11 w-11 md:h-12 md:w-12 grid place-items-center rounded-full border border-white/20 bg-ink-black/85 text-brand-white backdrop-blur transition-all duration-300 hover:border-brand-yellow hover:text-brand-yellow hover:scale-105 shadow-2xl"
              data-testid="ba-next"
            >
              <ChevronRight className="h-5 w-5 md:h-6 md:w-6" />
            </button>
          </>
        ) : null}
      </div>

      <div className="mt-5 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          {current.title ? (
            <h3 className="text-lg md:text-xl font-semibold tracking-tight text-brand-white">
              {current.title}
            </h3>
          ) : null}

          {current.caption ? (
            <p className="body-text text-sm mt-2 max-w-2xl">
              {current.caption}
            </p>
          ) : null}
        </div>

        {cleaned.length > 1 ? (
          <div className="flex items-center gap-4 shrink-0">
            <div className="flex items-center gap-2">
              {cleaned.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setActiveIndex(i)}
                  aria-label={`Go to comparison ${i + 1}`}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    activeIndex === i
                      ? "w-8 bg-brand-yellow"
                      : "w-2 bg-white/25 hover:bg-white/50"
                  }`}
                />
              ))}
            </div>

            <p className="shrink-0 text-xs font-bold uppercase tracking-[0.22em] text-brand-white/65">
              {String(activeIndex + 1).padStart(2, "0")} /{" "}
              {String(cleaned.length).padStart(2, "0")}
            </p>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function BeforeAfterCanvas({ before, after, showLabels }) {
  const wrapperRef = useRef(null);
  const draggingRef = useRef(false);
  const [position, setPosition] = useState(50);

  const updateFromClientX = useCallback((clientX) => {
    const node = wrapperRef.current;
    if (!node) return;

    const rect = node.getBoundingClientRect();
    const raw = ((clientX - rect.left) / rect.width) * 100;
    const clamped = Math.max(0, Math.min(100, raw));

    setPosition(clamped);
  }, []);

  useEffect(() => {
    const onMouseMove = (e) => {
      if (!draggingRef.current) return;
      updateFromClientX(e.clientX);
    };

    const onMouseUp = () => {
      draggingRef.current = false;
      document.body.style.userSelect = "";
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);

    return () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
  }, [updateFromClientX]);

  const startMouseDrag = (e) => {
    draggingRef.current = true;
    document.body.style.userSelect = "none";
    updateFromClientX(e.clientX);
  };

  useEffect(() => {
    const node = wrapperRef.current;
    if (!node) return undefined;

    const onTouchStart = (e) => {
      if (!e.touches || !e.touches.length) return;
      draggingRef.current = true;
      updateFromClientX(e.touches[0].clientX);
    };

    const onTouchMove = (e) => {
      if (!draggingRef.current) return;
      if (!e.touches || !e.touches.length) return;

      if (e.cancelable) {
        e.preventDefault();
      }

      updateFromClientX(e.touches[0].clientX);
    };

    const onTouchEnd = () => {
      draggingRef.current = false;
    };

    node.addEventListener("touchstart", onTouchStart, { passive: true });
    node.addEventListener("touchmove", onTouchMove, { passive: false });
    node.addEventListener("touchend", onTouchEnd, { passive: true });
    node.addEventListener("touchcancel", onTouchEnd, { passive: true });

    return () => {
      node.removeEventListener("touchstart", onTouchStart);
      node.removeEventListener("touchmove", onTouchMove);
      node.removeEventListener("touchend", onTouchEnd);
      node.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [updateFromClientX]);

  const onKeyDown = (e) => {
    if (e.key === "ArrowLeft") {
      setPosition((p) => Math.max(0, p - 3));
    }

    if (e.key === "ArrowRight") {
      setPosition((p) => Math.min(100, p + 3));
    }

    if (e.key === "Home") {
      setPosition(0);
    }

    if (e.key === "End") {
      setPosition(100);
    }
  };

  return (
    <div
      ref={wrapperRef}
      className="ba-canvas relative w-full overflow-hidden select-none border border-white/10 bg-ink-graphite shadow-2xl"
      style={{ aspectRatio: "16 / 10", touchAction: "pan-y" }}
      onMouseDown={startMouseDrag}
      role="group"
      aria-label="Before and after comparison"
    >
      {/* After image as full base layer */}
      <img
        src={after}
        alt="After"
        loading="lazy"
        draggable={false}
        className="absolute inset-0 h-full w-full object-cover pointer-events-none"
      />

      {/* Before image clipped from the left up to the divider */}
      <div
        className="absolute inset-0 overflow-hidden pointer-events-none"
        style={{ clipPath: `inset(0 ${100 - position}% 0 0)` }}
      >
        <img
          src={before}
          alt="Before"
          loading="lazy"
          draggable={false}
          className="absolute inset-0 h-full w-full object-cover"
        />
      </div>

      {showLabels ? (
        <>
          <span className="absolute top-4 left-4 md:top-5 md:left-5 z-10 pointer-events-none px-2.5 py-1.5 text-[10px] md:text-[11px] font-bold uppercase tracking-[0.24em] text-brand-white bg-ink-black/70 backdrop-blur-sm">
            Before
          </span>

          <span className="absolute top-4 right-4 md:top-5 md:right-5 z-10 pointer-events-none px-2.5 py-1.5 text-[10px] md:text-[11px] font-bold uppercase tracking-[0.24em] text-ink-black bg-brand-yellow">
            After
          </span>
        </>
      ) : null}

      <div
        className="absolute top-0 bottom-0 z-10"
        style={{ left: `${position}%`, transform: "translateX(-50%)" }}
      >
        <div className="ba-divider-line h-full w-[2px] bg-brand-white/90 shadow-[0_0_18px_rgba(0,0,0,0.55)]" />

        <button
          type="button"
          onKeyDown={onKeyDown}
          onMouseDown={(e) => {
            e.stopPropagation();
            startMouseDrag(e);
          }}
          aria-label="Drag to compare before and after"
          className="ba-handle absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-12 w-12 md:h-14 md:w-14 grid place-items-center rounded-full border-2 border-brand-white bg-ink-black/80 text-brand-white shadow-2xl backdrop-blur-sm cursor-ew-resize transition-transform duration-200 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-brand-yellow"
        >
          <MoveHorizontal className="h-5 w-5 md:h-6 md:w-6" />
        </button>
      </div>
    </div>
  );
}